import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TechnicalInspectionService } from '@app/technical-inspections/_services/technical-inspection.service';
import { AvailableTimeTechPoint } from '@app/technical-inspections-cc/_models/available-time-tech-point.model';
import { AvailableTimeSlot } from '@app/technical-inspections-cc/_models/available-time-slot.model';
import { TechPointService } from '@app/tech-points/_models/tech-point-service.model';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { RvsCategory } from '@models/rvs-category.model';
import { prepareDateForDb, prepareDateForForm } from '@app/_utils/date-util';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { StatusCode } from '@app/_enums/status-code';
import { Status } from '@models/status.model';
import { NomenclatureService } from '@services/nomenclature.service';

interface SlotEntry {
  slot: AvailableTimeSlot;
  selected: boolean;
}

@Component({
  selector: 'app-mass-create-technical-inspections',
  templateUrl: './mass-create-technical-inspections.component.html',
  styleUrls: ['./mass-create-technical-inspections.component.css']
})
export class MassCreateTechnicalInspectionsComponent implements OnInit, OnDestroy {

  // Constants
  readonly CODE_RESERVED = 'RESERVED';
  readonly MAIN_SERVICE_GTP_NAME = 'ГОДИШЕН ТЕХНИЧЕСКИ ПРЕГЛЕД';
  readonly BASIC_SERVICE_NAME = 'ГТП';
  readonly CITY_CODE_SOFIA = 'SOF';
  readonly ORDER_OPTION_DATE = 'date';
  readonly INACTIVE = 'INACTIVE';

  // Forms
  reservationForm: FormGroup;

  // Data
  cities: any[] = [];
  rvsCategories: RvsCategory[] = [];
  technicalPoints: any[] = [];
  mainServices: any[] = [];
  techInspStatuses: Status[];
  gtpMainService: any;
  basicTechPointService: TechPointService | null = null;

  // Slot selection
  slotEntries: SlotEntry[] = [];
  selectAll = false;

  // State
  isSlotsLoading = false;
  isCreating = false;
  isCardOpen = false;
  submittedForm = false;
  dataLoaded = false;

  // Results
  results: { succeeded: number; failed: number; total: number } | null = null;

  // Min date for datepicker
  minDate: { year: number; month: number; day: number };

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private techInspService: TechnicalInspectionService,
    private techPointService: TechnicalPointService,
    private nomenclatureService: NomenclatureService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {
    const today = new Date();
    this.minDate = { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };

    this.reservationForm = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(16)]],
      email: ['', [Validators.required, Validators.maxLength(60)]],
      name: ['', [Validators.required, Validators.maxLength(60)]],
      regNumber: ['', [Validators.required, Validators.maxLength(16)]],
      rvsCategory: [null, Validators.required],
      searchDate: [''],
      searchTechPoint: [null]
    });
  }

  get mainForm() { return this.reservationForm.controls; }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadInitialData(): void {
    const defCityCode = this.CITY_CODE_SOFIA;
    this.nomenclatureService.getStatusesByType(StatusCode.TechnicalInspection).subscribe(statuses => {
      this.techInspStatuses = statuses;
    });

    this.techPointService.findTechnicalPoints().subscribe(technicalPoints => {
      this.technicalPoints = technicalPoints.filter(tp => tp.status?.code !== this.INACTIVE);
    });

    this.techInspService.getMainServicesByCityCode(defCityCode).subscribe(response => {
      this.mainServices = response.services;
      this.gtpMainService = this.mainServices.find((s: any) => s.name === this.MAIN_SERVICE_GTP_NAME);

      if (this.gtpMainService) {
        this.techInspService.getRvsCategoriesByServiceMobileApp(this.gtpMainService.id, defCityCode).subscribe(rvsCategories => {
          this.rvsCategories = rvsCategories;
          if (this.rvsCategories.length > 0) {
            this.reservationForm.get('rvsCategory')?.patchValue(this.rvsCategories[0]);
          }
          this.dataLoaded = true;
        });
      } else {
        this.dataLoaded = true;
      }
    });
  }

  onRvsCategoryChange(): void {
    this.clearSlots();
  }

  onDateOrTechPointChange(): void {
    const searchDate = this.reservationForm.get('searchDate')?.value;
    const searchTechPoint = this.reservationForm.get('searchTechPoint')?.value;
    const rvsCategory = this.reservationForm.get('rvsCategory')?.value;

    if (!searchDate || !searchTechPoint || !rvsCategory) {
      return;
    }

    this.loadAvailableSlots(searchDate, searchTechPoint, rvsCategory);
  }

  private loadAvailableSlots(searchDate: any, techPoint: any, rvsCategory: RvsCategory): void {
    this.isSlotsLoading = true;
    this.slotEntries = [];
    this.selectAll = false;
    this.results = null;

    const searchDto = {
      date: prepareDateForDb(searchDate),
      hasAssistant: false,
      slots: [],
      cityCode: techPoint.city?.code || this.CITY_CODE_SOFIA,
      categoryId: rvsCategory.id,
      orderOption: this.ORDER_OPTION_DATE,
      techPointMobileAppId: techPoint.mobileAppId,
      services: [{ id: this.gtpMainService.id }]
    };

    this.techInspService.getTechPointsAvailableTimeSlots(searchDto as any).subscribe(
      (availableTimeTechPoints: AvailableTimeTechPoint[]) => {
        const matchingTp = availableTimeTechPoints.length > 0 ? availableTimeTechPoints[0] : null;
        const slots = matchingTp?.slots || [];
        this.slotEntries = slots
          .filter(s => s.available)
          .map(s => ({ slot: s, selected: false }));

        this.loadBasicService(rvsCategory, techPoint.id);
        this.isSlotsLoading = false;
      },
      () => {
        this.isSlotsLoading = false;
        this.toastr.error(this.translate.instant('errors.commonServerError'));
      }
    );
  }

  private loadBasicService(rvsCategory: RvsCategory, techPointId: number): void {
    this.techInspService.getTechPointServicesByType(1, rvsCategory.id, techPointId).subscribe(
      (services: TechPointService[]) => {
        this.basicTechPointService = services.find(s => s.serviceName === this.MAIN_SERVICE_GTP_NAME) || services[0] || null;
      }
    );
  }

  toggleSelectAll(): void {
    this.slotEntries.forEach(entry => entry.selected = this.selectAll);
  }

  onSlotToggle(): void {
    this.selectAll = this.slotEntries.length > 0 && this.slotEntries.every(entry => entry.selected);
  }

  getSelectedCount(): number {
    return this.slotEntries.filter(e => e.selected).length;
  }

  async createReservations(): Promise<void> {
    this.submittedForm = true;

    if (this.reservationForm.invalid) {
      return;
    }

    const selectedSlots = this.slotEntries.filter(e => e.selected);
    if (selectedSlots.length === 0) {
      return;
    }

    this.isCreating = true;
    this.results = { succeeded: 0, failed: 0, total: selectedSlots.length };

    const formValue = this.reservationForm.value;

    for (const entry of selectedSlots) {
      const reservation = this.constructReservation(formValue, entry.slot);
      try {
        await this.techInspService.saveTechnicalInspection(reservation as any, false).toPromise();
        this.results.succeeded++;
      } catch (err) {
        this.results.failed++;
        console.error('Failed to create reservation for slot', entry.slot.id, err);
      }
    }

    this.isCreating = false;

    if (this.results.failed === 0) {
      this.toastr.success(this.translate.instant('massCreate.allSucceeded'));
    } else {
      this.toastr.warning(
        this.translate.instant('massCreate.partialSuccess', {
          succeeded: this.results.succeeded,
          failed: this.results.failed
        })
      );
    }
  }

  private constructReservation(formValue: any, slot: AvailableTimeSlot): any {
    return {
      id: null,
      email: formValue.email,
      name: formValue.name,
      status: this.techInspStatuses.find(status => status.code === this.CODE_RESERVED) || null,
      regNumber: formValue.regNumber,
      registrationCertNumber: null,
      rvsCategory: formValue.rvsCategory,
      versionData: null,
      inspectionDate: prepareDateForDb(formValue.searchDate),
      technicalPointId: formValue.searchTechPoint.id,
      techPointWorkingHourId: slot.id,
      contractRvsId: null,
      phoneNumber: this.formatPhoneNumber(formValue.phoneNumber),
      createdOrder: true,
      incomingWay: 'erp_cc',
      customerDiscount: null,
      customerCard: null,
      promotionId: null,
      promoCode: null,
      isEmployee: false,
      isBankPayment: false,
      isDeferPayment: false,
      techPointServices: this.basicTechPointService ? [this.basicTechPointService] : [],
      techInspectionOrders: [],
      techInspectionTimes: [],
      skipSubscribtion: true,
      isAgreeWithNotification: false,
      wantsMarketingMessages: false,
      isAppliedVoucherOrPromocode: false
    };
  }

  private formatPhoneNumber(value: string): string {
    if (!value) return value;
    let cleaned = value.replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+359')) {
      return cleaned;
    }
    if (cleaned.startsWith('08') || cleaned.startsWith('09')) {
      return '+359' + cleaned.substring(1);
    }
    return cleaned;
  }

  private clearSlots(): void {
    this.slotEntries = [];
    this.selectAll = false;
    this.results = null;
    this.reservationForm.get('searchTechPoint')?.patchValue(null);
  }

  compareById(a: any, b: any): boolean {
    return a && b && a.id === b.id;
  }
}
